import { useState } from 'react';
import shopifyCart, { buildBlendDisplaySnapshot } from '../services/shopify';
import { sanitizeBlendName } from '../utils/blendName';

/**
 * Shared cart action state for inline + sticky Add to Cart controls.
 */
export function useBlendCartAction({
  blendResult,
  packetSize,
  selectedIngredients,
  preparationMethod,
  ingredients,
  mode = 'auto',
  customPercentages = null,
}) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [lastAttempt, setLastAttempt] = useState(0);
  const [blendName, setBlendName] = useState('');

  const ready = Boolean(blendResult?.success);
  const isShopifySynced = Boolean(
    ready && blendResult.variant_id && blendResult.product_id,
  );

  let userRatiosBlocker = null;
  if (ready && mode === 'user_ratios') {
    const pcts = customPercentages || {};
    if (selectedIngredients.length < 2) {
      userRatiosBlocker = 'Select at least 2 ingredients.';
    } else if (selectedIngredients.some((id) => !(Number(pcts[id]) > 0))) {
      userRatiosBlocker = 'Set a percentage for every ingredient.';
    } else {
      const total = selectedIngredients.reduce(
        (sum, id) => sum + Number(pcts[id] || 0),
        0,
      );
      if (Math.abs(total - 100) > 1e-9) {
        const off = Math.round(total * 10) / 10;
        userRatiosBlocker = `Percentages must add up to 100% (currently ${off}%).`;
      }
    }
  }

  const disabled = !ready || adding || !isShopifySynced || Boolean(userRatiosBlocker);
  const tooltip = !ready
    ? ''
    : !isShopifySynced
      ? 'Products are being set up. Please try again shortly.'
      : userRatiosBlocker || '';

  const handleAddToCart = async () => {
    if (!ready) return;
    if (!isShopifySynced) {
      setError('Products are being set up. Please try again in a few minutes.');
      return;
    }
    if (userRatiosBlocker) {
      setError(userRatiosBlocker);
      return;
    }

    const now = Date.now();
    if (now - lastAttempt < 2000) {
      setError('Please wait a moment before trying again.');
      return;
    }
    setLastAttempt(now);
    setAdding(true);
    setError(null);

    try {
      setStatus('Adding to cart...');
      await shopifyCart.addCustomBlend({
        selectedIngredients,
        ingredients,
        variant_id: blendResult.variant_id,
        product_id: blendResult.product_id,
        packet_size: packetSize,
        preparation_method: preparationMethod,
        mode,
        custom_percentages: mode === 'user_ratios' ? customPercentages : null,
        blend_name: sanitizeBlendName(blendName),
        display_snapshot: buildBlendDisplaySnapshot(
          blendResult,
          packetSize,
          preparationMethod,
        ),
      });
      setStatus('Added to cart!');
      setTimeout(() => {
        setStatus('');
        setAdding(false);
      }, 2000);
    } catch (err) {
      console.error('Error adding to cart:', err);
      if (err.response?.status === 429 || err.message?.includes('Rate limit')) {
        setError('Too many requests to Shopify. Please wait 30 seconds and try again.');
      } else if (
        err.message
        && (err.message.includes('fetch')
          || err.message.includes('NetworkError')
          || err.message.includes('Failed to fetch'))
      ) {
        setError(
          'Unable to connect to store. Please check your internet connection or try again later.',
        );
      } else {
        setError(err.message || 'Failed to add to cart. Please try again.');
      }
      setAdding(false);
      setStatus('');
    }
  };

  return {
    ready,
    adding,
    error,
    status,
    blendName,
    setBlendName,
    isShopifySynced,
    userRatiosBlocker,
    disabled,
    tooltip,
    handleAddToCart,
  };
}
